import {
  assessmentAmount,
  assessmentValues,
  assessmentYear as coreAssessmentYear,
  reconcileAssessment,
} from './assessment-reconciliation';
import type { AssessmentRoll, AssessorRecord, RecorderSnapshot } from './types';

export { assessmentAmount, assessmentValues };

export function assessmentYear(year: string) {
  return coreAssessmentYear(year);
}

export function assessmentYoy(current: number | null, previous: number | null) {
  const change =
    current !== null && previous !== null && previous > 0 ? (current - previous) / previous : null;
  return change !== null && Number.isFinite(change) ? change : null;
}

export function assessmentHistory(history: AssessmentRoll[] = []) {
  const counts = new Map<number, number>();
  for (const roll of history) {
    const year = assessmentYear(roll.year);
    if (year !== null) counts.set(year, (counts.get(year) ?? 0) + 1);
  }
  const rows = history.map((roll, index) => ({
    roll,
    key: `${roll.rowId}-${index}`,
    year: assessmentYear(roll.year),
    values: assessmentValues(roll.fields),
  }));
  return rows
    .sort((a, b) => (b.year ?? -1) - (a.year ?? -1))
    .map((row) => {
      const previous =
        row.year !== null && counts.get(row.year) === 1 && counts.get(row.year - 1) === 1
          ? rows.find((candidate) => candidate.year === row.year! - 1)
          : undefined;
      const reason =
        row.year === null
          ? 'unknownYear'
          : counts.get(row.year)! > 1 || counts.get(row.year - 1)! > 1
            ? 'multipleRows'
            : !previous
              ? 'missingPrevious'
              : row.values.total === null || previous.values.total === null
                ? 'missingValue'
                : previous.values.total === 0
                  ? 'zeroPrevious'
                  : null;
      return {
        ...row,
        ambiguous: row.year !== null && counts.get(row.year)! > 1,
        previous,
        reason,
        yoy:
          reason === null ? assessmentYoy(row.values.total, previous?.values.total ?? null) : null,
      };
    });
}

export function assessmentFieldEntries(fields: Record<string, string>) {
  return Object.entries(fields)
    .sort(
      ([a], [b]) =>
        Number(/(?:assessed .* value|exemption)/i.test(b)) -
        Number(/(?:assessed .* value|exemption)/i.test(a)),
    )
    .map(([label, value]) => ({
      label,
      value,
      money: /(?:assessed .* value|exemption value)/i.test(label),
      amount: assessmentAmount(value),
    }));
}

export function assessmentView(
  assessor: AssessorRecord | null | undefined,
  recorder?: RecorderSnapshot | null,
) {
  const rows = assessmentHistory(assessor?.history);
  const latest = rows[0]?.year !== null && !rows[0]?.ambiguous ? rows[0] : undefined;
  const current =
    latest?.values ?? (rows.length === 0 && assessor ? assessmentValues(assessor.fields) : null);
  const reconciliation = reconcileAssessment(assessor, recorder);
  return {
    rows,
    latest,
    current,
    reconciliation,
    diverged: reconciliation.status === 'reassessed' || reconciliation.status === 'reduced',
  };
}
